import React, { useEffect, useState } from "react";
import { Star, X, Loader, Bookmark, ArrowRight, TrendingUp } from "lucide-react";
import {
  getWatchlistItems,
  removeFromWatchlist,
  getCachedSnapshotDoc,
  extractAIMetrics,
} from "../../services/researchService";
import { getCurrentUid } from "../../services/disclosureService";

/**
 * WatchlistGrid — replaces the old AnalystRatingsModule mock cards.
 *
 * Shows the current user's saved-ticker list as a card grid. Click a
 * card → opens the StockResearchModal (parent handles the modal state
 * and passes it down via the onOpenTicker callback).
 *
 * Empty state: friendly "save your first ticker" prompt plus a row of
 * 3-5 starter chips. Clicking a starter chip opens the modal for that
 * ticker so users can preview the experience without committing to
 * the watchlist yet.
 *
 * Live updates: we read the watchlist from Firestore on mount and also
 * re-read on the `watchlistUpdated` window event so the grid refreshes
 * the instant the user saves a new ticker from inside the modal.
 *
 * Props:
 *   onOpenTicker - Required. Called with a ticker string when the
 *                  user clicks a card or a starter chip.
 */

const STARTER_TICKERS = ["NVDA", "AAPL", "MSFT", "GOOGL", "META"];

const WatchlistGrid = ({ onOpenTicker }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null); // ticker mid-removal

  const refresh = async () => {
    const uid = getCurrentUid();
    if (!uid) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const next = await getWatchlistItems(uid);
      setItems(next);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    let cancelled = false;
    const uid = getCurrentUid();
    if (!uid) {
      setItems([]);
      setLoading(false);
      return;
    }
    getWatchlistItems(uid)
      .then((next) => {
        if (!cancelled) {
          setItems(next);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Cross-component sync: the modal dispatches `watchlistUpdated`
  // whenever Save/Remove is clicked. We listen for it so the grid
  // updates without needing the user to navigate away and back.
  useEffect(() => {
    const onUpdate = () => refresh();
    window.addEventListener("watchlistUpdated", onUpdate);
    return () => window.removeEventListener("watchlistUpdated", onUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemove = async (ticker, e) => {
    e.stopPropagation(); // don't open the modal on the X click
    const uid = getCurrentUid();
    if (!uid) return;
    setRemoving(ticker);
    try {
      const next = await removeFromWatchlist(uid, ticker);
      setItems(next);
    } finally {
      setRemoving(null);
    }
  };

  /* ─── Render ────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="surface-xl p-10 flex items-center justify-center gap-3 text-arc-muted">
        <Loader className="animate-spin" size={18} />
        Loading watchlist…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="surface-xl p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-arc-gold/30 bg-arc-gold/5 mb-4">
          <Bookmark size={20} className="text-arc-gold" />
        </div>
        <h3 className="text-lg font-semibold text-arc-ink mb-2">
          Your watchlist is empty
        </h3>
        <p className="text-sm text-arc-muted max-w-md mx-auto mb-5 leading-relaxed">
          Search any U.S.-listed ticker above and tap{" "}
          <span className="inline-flex items-center gap-1 text-arc-gold">
            <Star size={11} className="inline" /> Save to watchlist
          </span>{" "}
          inside the report. Saved tickers show up here for instant
          re-access. Or try one of these to get started:
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {STARTER_TICKERS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onOpenTicker?.(t)}
              className="btn-tactile inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-arc-line bg-arc-surface/40 text-xs font-mono text-arc-ink hover:border-arc-gold/40 hover:bg-arc-gold/5 transition-all"
            >
              {t}
              <ArrowRight size={11} className="text-arc-dim" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Star size={14} className="text-arc-gold fill-arc-gold" />
        <p className="eyebrow">Your watchlist · {items.length}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((item) => (
          <WatchlistCard
            key={item.ticker}
            ticker={item.ticker}
            addedAt={item.addedAt}
            onOpen={() => onOpenTicker?.(item.ticker)}
            onRemove={(e) => handleRemove(item.ticker, e)}
            removing={removing === item.ticker}
          />
        ))}
      </div>
    </div>
  );
};

/* ─── Single card ─────────────────────────────────────────────── */

/**
 * WatchlistCard — rich preview for a saved ticker.
 *
 * Fetches the cached snapshot doc directly from Firestore on mount,
 * extracts AI metrics from the cached analysis text, and renders a
 * dense one-glance summary: Risk Character chip (color-coded),
 * ARCrypto AI stance line, conviction score, latest revenue + YoY,
 * and placeholders for 7d / 30d / YTD price percentages (Phase 4
 * Polygon integration will fill these in).
 *
 * Three render states:
 *   - "loading"     — Firestore read in flight
 *   - "researched"  — snapshot found, metrics parsed, full card
 *   - "unresearched" — no cached snapshot yet (user added it but
 *                     never opened the modal). Shows a CTA.
 */
const WatchlistCard = ({ ticker, addedAt, onOpen, onRemove, removing }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCachedSnapshotDoc(ticker)
      .then((doc) => {
        if (cancelled) return;
        setSnapshot(doc);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  const metrics = extractAIMetrics(snapshot?.ai?.text);
  const latest = snapshot?.fundamentals?.latest;
  const name = snapshot?.name;
  const sector = snapshot?.sector;
  const asOfDate = snapshot?.fundamentals?.asOfDate;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="btn-tactile group relative text-left w-full rounded-xl border border-arc-line bg-arc-surface/30 hover:border-arc-gold/40 hover:bg-arc-gold/[0.03] transition-all p-4 flex flex-col gap-3"
    >
      {/* Remove button — overlays the card, click stops propagation */}
      <span
        role="button"
        tabIndex={-1}
        aria-label={`Remove ${ticker} from watchlist`}
        onClick={onRemove}
        className="absolute top-2 right-2 p-1 rounded-md text-arc-dim opacity-0 group-hover:opacity-100 hover:text-red-300 hover:bg-arc-bg transition-all"
      >
        {removing ? (
          <Loader size={12} className="animate-spin" />
        ) : (
          <X size={12} />
        )}
      </span>

      {/* Header: ticker + sector + name */}
      <div>
        <div className="flex items-baseline gap-2">
          <h3 className="text-xl font-bold text-arc-ink tracking-tight font-mono">
            {ticker}
          </h3>
          {sector && (
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim">
              {sector}
            </span>
          )}
          <Star size={11} className="text-arc-gold fill-arc-gold ml-auto mr-5" />
        </div>
        {name && (
          <p className="text-xs text-arc-muted truncate mt-0.5">{name}</p>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="py-3 text-[11px] text-arc-dim inline-flex items-center gap-2">
          <Loader size={11} className="animate-spin" />
          Loading…
        </div>
      )}

      {/* Unresearched state (no cached snapshot yet) */}
      {!loading && !snapshot && (
        <div className="py-3 rounded-lg border border-dashed border-arc-line bg-arc-bg/40 text-center">
          <p className="text-xs text-arc-muted leading-relaxed">
            No research yet for {ticker}.<br />
            <span className="text-arc-gold">Click to generate</span> — first
            run takes ~60-90s, then cached forever.
          </p>
        </div>
      )}

      {/* Full researched state */}
      {!loading && snapshot && (
        <>
          {/* Risk Character chip (color-coded) */}
          {metrics.riskCharacter && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim mb-1.5">
                ARCrypto Risk Character
              </p>
              <RiskChip character={metrics.riskCharacter} />
            </div>
          )}

          {/* ARCrypto AI Stance line */}
          {(metrics.finalStance || metrics.compositeScore != null) && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim mb-1">
                ARCrypto AI Stance
              </p>
              <p className="text-xs text-arc-ink leading-snug">
                {metrics.finalStance && (
                  <span className="font-semibold">{metrics.finalStance}</span>
                )}
                {metrics.compositeScore != null && (
                  <>
                    {" · "}
                    <span className="font-mono text-arc-gold">
                      {metrics.compositeScore.toFixed(2)} / 5.0
                    </span>
                  </>
                )}
                {metrics.convictionBand && (
                  <span className="block text-[11px] text-arc-muted mt-0.5">
                    {metrics.convictionBand}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Fundamentals quick stats */}
          {latest && (
            <div className="flex gap-3 text-[11px] py-2 border-t border-arc-line/40">
              <Stat label="Revenue" value={formatBigDollars(latest.revenue)} />
              <Stat
                label="YoY"
                value={formatPercent(latest.revenueYoY)}
                color={signColor(latest.revenueYoY)}
              />
              <Stat
                label="Op Margin"
                value={formatPercent(latest.operatingMargin)}
              />
            </div>
          )}

          {/* Price percentages — Phase 4 (Polygon) placeholders */}
          <div className="flex gap-3 text-[11px] py-2 border-t border-arc-line/40">
            <Stat
              label="7d"
              value="—"
              hint="price feed pending"
            />
            <Stat label="30d" value="—" hint="price feed pending" />
            <Stat label="YTD" value="—" hint="price feed pending" />
          </div>
        </>
      )}

      {/* Footer: timestamps + arrow */}
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wide text-arc-dim mt-auto pt-1">
        <span>
          {asOfDate
            ? `data as of ${asOfDate}`
            : addedAt
            ? `saved ${formatRelative(addedAt)}`
            : "saved"}
        </span>
        <span className="inline-flex items-center gap-1 text-arc-gold group-hover:translate-x-0.5 transition-transform">
          open
          <ArrowRight size={11} />
        </span>
      </div>
    </button>
  );
};

/* ─── Small UI bits used inside the card ─────────────────────── */

const RISK_COLORS = {
  Conservative: {
    text: "text-emerald-300",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/40",
    dot: "bg-emerald-400",
  },
  Moderate: {
    text: "text-amber-300",
    bg: "bg-amber-500/10",
    border: "border-amber-500/40",
    dot: "bg-amber-400",
  },
  Aggressive: {
    text: "text-red-300",
    bg: "bg-red-500/10",
    border: "border-red-500/40",
    dot: "bg-red-400",
  },
};

const RiskChip = ({ character }) => {
  const color = RISK_COLORS[character] || RISK_COLORS.Moderate;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium ${color.text} ${color.bg} ${color.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${color.dot}`} />
      {character}
    </span>
  );
};

const Stat = ({ label, value, color, hint }) => (
  <div className="flex-1" title={hint || ""}>
    <p className="text-[9px] font-mono uppercase tracking-[0.14em] text-arc-dim mb-0.5">
      {label}
    </p>
    <p className={`text-xs font-mono ${color || "text-arc-ink"}`}>{value}</p>
  </div>
);

function signColor(n) {
  if (n == null) return "text-arc-muted";
  if (n > 0) return "text-emerald-300";
  if (n < 0) return "text-red-300";
  return "text-arc-muted";
}

function formatBigDollars(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toLocaleString()}`;
}

function formatPercent(p) {
  if (p == null) return "—";
  const v = p * 100;
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(1)}%`;
}

function formatRelative(iso) {
  if (!iso) return "";
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return "";
  const delta = Date.now() - then;
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default WatchlistGrid;
